import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, AlertCircle, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, errMsg, mediaUrl } from "../lib/api";
import { useAuth, useCart } from "../context/Store";

const FIELDS = [
  { name: "name", label: "Full name", placeholder: "Priya Kumar" },
  { name: "phone", label: "Phone (10 digits)", placeholder: "9790007557" },
  { name: "line", label: "Address", placeholder: "Flat, street, area" },
  { name: "city", label: "City", placeholder: "Coimbatore" },
  { name: "state", label: "State", placeholder: "Tamil Nadu" },
  { name: "pincode", label: "Pincode", placeholder: "641001" },
];

export default function Checkout() {
  const { user } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", line: "", city: "", state: "", pincode: "" });
  const [busy, setBusy] = useState(false);
  const [offlineOrder, setOfflineOrder] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);

  if (!user) {
    navigate("/auth?next=/checkout");
    return null;
  }

  const discount = coupon?.discount || 0;
  const goods = cart.subtotal - discount;
  const shipping = cart.items.length === 0 || goods >= 999 ? 0 : 79;
  const total = goods + shipping;

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponCode.trim() });
      setCoupon(data);
      toast.success(data.message);
    } catch (err) {
      setCoupon(null);
      toast.error(errMsg(err, "Invalid coupon"));
    } finally {
      setCouponBusy(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        address: form,
        return_url: window.location.origin + "/payment-result",
        coupon_code: coupon?.code || undefined,
      });
      if (!data.payments_configured) {
        setOfflineOrder(data.order_id);
        return;
      }
      const { load } = await import("@cashfreepayments/cashfree-js");
      const cf = await load({ mode: data.mode || "sandbox" });
      await cf.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (err) {
      toast.error(errMsg(err, "Could not start payment"));
      setBusy(false);
    }
  };

  if (offlineOrder) {
    return (
      <div className="max-w-xl mx-auto px-4 pt-40 pb-32 text-center" data-testid="checkout-offline">
        <AlertCircle className="h-12 w-12 text-brand-blue mx-auto" />
        <h1 className="font-display text-3xl font-bold mt-6">Order placed — payment coming online</h1>
        <p className="text-brand-ink/60 mt-4 leading-relaxed">
          Your order <span className="font-semibold text-brand-ink">{offlineOrder}</span> is saved.
          Online payments are being activated on our payment gateway. Our team will contact you
          to complete payment, or check your orders page shortly.
        </p>
        <Link to="/account" className="inline-block mt-8 rounded-full brand-gradient-bg text-white px-8 py-3.5 text-sm font-semibold" data-testid="offline-orders-link">
          View my orders
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-28 md:pt-36 pb-24" data-testid="checkout-page">
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Checkout</h1>
      {cart.items.length === 0 ? (
        <div className="text-center py-24" data-testid="checkout-empty">
          <p className="font-display text-2xl text-brand-ink/50">Your cart is empty</p>
          <Link to="/shop" className="inline-block mt-6 rounded-full brand-gradient-bg text-white px-8 py-3.5 text-sm font-semibold">Back to shop</Link>
        </div>
      ) : (
        <form onSubmit={pay} className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-3">
            <h2 className="font-display text-xl font-bold mb-6">Delivery address</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.name === "line" ? "sm:col-span-2" : ""}>
                  <label className="text-xs font-semibold uppercase tracking-wider text-brand-ink/50">{f.label}</label>
                  <input
                    data-testid={`address-${f.name}`}
                    required
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    placeholder={f.placeholder}
                    className="mt-2 w-full h-12 rounded-2xl bg-brand-ink/5 px-4 text-sm outline-none focus:ring-2 focus:ring-brand-cyan"
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 rounded-3xl bg-brand-ink/[0.03] p-8 h-fit">
            <h2 className="font-display text-xl font-bold">Your order</h2>
            <div className="mt-5 space-y-4">
              {cart.items.map(({ product: p, qty }) => (
                <div key={`${p.id}:${p.variant_id || "base"}`} className="flex items-center gap-3 text-sm">
                  <img src={mediaUrl(p.images?.[0])} alt="" className="h-12 w-12 rounded-xl object-cover" />
                  <span className="flex-1 min-w-0 font-medium">{p.name}{p.selected_variant && <small className="block text-xs text-brand-ink/50">{p.selected_variant}</small>} × {qty}</span>
                  <span className="font-semibold">{fmt(p.price * qty)}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-brand-ink/10">
              {coupon ? (
                <div className="flex items-center justify-between rounded-2xl bg-brand-cyan/10 border border-brand-cyan/30 px-4 py-3" data-testid="coupon-applied">
                  <span className="flex items-center gap-2 text-sm font-semibold text-brand-blue"><Tag className="h-4 w-4" /> {coupon.code} — {fmt(discount)} off</span>
                  <button type="button" data-testid="coupon-remove" onClick={() => { setCoupon(null); setCouponCode(""); }} className="p-1 text-brand-ink/40 hover:text-rose-500"><X className="h-4 w-4" /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    data-testid="coupon-input"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                    className="flex-1 h-11 rounded-full bg-white border border-brand-ink/10 px-4 text-sm outline-none focus:ring-2 focus:ring-brand-cyan"
                  />
                  <button type="button" data-testid="coupon-apply" onClick={applyCoupon} disabled={couponBusy}
                    className="h-11 rounded-full border-2 border-brand-ink px-5 text-sm font-semibold hover:bg-brand-ink hover:text-white transition-colors disabled:opacity-50">
                    {couponBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                  </button>
                </div>
              )}
            </div>

            <div className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-brand-ink/60">Subtotal</span><span>{fmt(cart.subtotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-brand-blue font-semibold" data-testid="discount-row"><span>Coupon discount</span><span>−{fmt(discount)}</span></div>}
              <div className="flex justify-between"><span className="text-brand-ink/60">Shipping</span><span>{shipping === 0 ? "Free" : fmt(shipping)}</span></div>
              <div className="flex justify-between text-base pt-2 border-t border-brand-ink/10"><span className="font-semibold">Total</span><span className="font-display font-bold text-xl" data-testid="checkout-total">{fmt(total)}</span></div>
            </div>
            <button type="submit" disabled={busy} data-testid="pay-now-button"
              className="mt-8 w-full h-13 py-4 rounded-full brand-gradient-bg text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-[0_8px_30px_rgba(41,98,255,0.35)] hover:scale-[1.01] transition-transform disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {busy ? "Opening secure checkout..." : "Pay securely with Cashfree"}
            </button>
            <p className="mt-4 text-xs text-brand-ink/40 text-center">UPI · Cards · Net banking · Wallets</p>
          </div>
        </form>
      )}
    </div>
  );
}
